import { Settings, SettingsKey, SettingsVersion } from './user-setting.model';
import {
    defaultLocale,
    getBrowserLocale,
    supportedLocales
} from '../../settings/state/settings.store';
import { Currency } from '../../../shared/components/currency-selector/currency-country-codes';
import { Instrument } from '../../../shared/state/instruments/instrument.model';
import { Acct } from '../../../shared/state/accts/acct.model';
import { AccountIssuer } from '../../../pages/admin/state/account-issuers/account-issuer.model';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { PortfolioOptimizationInput } from '../../portfolio-optimization/state/portfolio-optimization.model';

export const DEFAULT_SETTINGS = {
    LOCALE: {
        version: [1, 0] as SettingsVersion,
        value:
            supportedLocales.indexOf(getBrowserLocale()) > -1
                ? getBrowserLocale()
                : defaultLocale
    },
    HIDE_PRO_TIP: {
        version: [1, 0] as SettingsVersion,
        value: false
    },
    HIDE_CONFIRM_DIALOG: {
        version: [1, 0] as SettingsVersion,
        value: false
    },
    THEME: {
        version: [1, 0] as SettingsVersion,
        value: 'light' as 'light' | 'dark' | 'auto' | string
    },
    DIGITS_PERCENT: {
        version: [1, 0] as SettingsVersion,
        value: [0, 2] as [number, number]
    },
    DIGITS_SMALL: {
        version: [1, 0] as SettingsVersion,
        value: [0, 4] as [number, number]
    },
    DIGITS_LARGE: {
        version: [1, 0] as SettingsVersion,
        value: [0, 2] as [number, number]
    },
    DIGITS_FIXED: {
        version: [1, 0] as SettingsVersion,
        value: [0, 2] as [number, number]
    },
    CURRENCY_SYMBOL: {
        version: [1, 0] as SettingsVersion,
        value: 'code' as 'code' | 'symbol' | 'symbol-narrow'
    },
    BASE_CURRENCY: {
        version: [1, 0] as SettingsVersion,
        value: 'SEK' as Currency
    },
    TABLE_SORT_ORDER: {
        version: [1, 0] as SettingsVersion,
        value: Array<{ key: string; order: 'asc' | 'desc' }>
    },
    TABLE_DIMENSIONS: {
        version: [1, 0] as SettingsVersion,
        value: {} as { [key: string]: { active: boolean; order: number } }
    },
    TABLE_METRICS: {
        version: [1, 0] as SettingsVersion,
        value: {} as { [key: string]: { active: boolean; order: number } }
    },
    TABLE_GROUP_BY: {
        version: [1, 0] as SettingsVersion,
        value: [null] as Array<string>
    },
    TABLE_SETTINGS: {
        version: [1, 0] as SettingsVersion,
        value: {} as { [key: string]: any }
    },
    INSTRUMENT_TYPE_FILTER: {
        version: [1, 0] as SettingsVersion,
        value: [] as Array<Instrument['type']>
    },
    ACCOUNT_TYPE_FILTER: {
        version: [1, 0] as SettingsVersion,
        value: [] as Array<Acct['accountType']>
    },
    ISSUER_FILTER: {
        version: [1, 0] as SettingsVersion,
        value: [] as Array<AccountIssuer['accountIssuerId']>
    },
    LEGAL_ENTITY_FILTER: {
        version: [1, 0] as SettingsVersion,
        value: [] as Array<LegalEntity['ownerId']>
    },
    INSTRUMENT_FILTER: {
        version: [1, 0] as SettingsVersion,
        value: [] as Array<Instrument['instrumentId']>
    },
    PAGE_LENGTH: {
        version: [1, 0] as SettingsVersion,
        value: 20 as number
    },
    TABLE_COLUMN_ORDER: {
        version: [1, 0] as SettingsVersion,
        value: [] as Array<string>
    },
    INCLUDE_DIVIDEND_IN_TOTAL_PNL: {
        version: [1, 0] as SettingsVersion,
        value: {} as { [key: string]: any }
    },
    ACTIVE_DIMENSION: {
        version: [1, 0] as SettingsVersion,
        value: null as string
    },
    PORTFOLIO_OPTIMIZATION_GROUPS: {
        version: [1, 0] as SettingsVersion,
        value: [] as PortfolioOptimizationInput['groups']
    },
    SHOW_SOLD_OFF: {
        version: [1, 0] as SettingsVersion,
        value: false
    }
};

export const DEFAULT_GLOBAL_SETTINGS = {
    LOCALE: DEFAULT_SETTINGS.LOCALE,
    THEME: DEFAULT_SETTINGS.THEME,
    DIGITS_PERCENT: DEFAULT_SETTINGS.DIGITS_PERCENT,
    DIGITS_SMALL: DEFAULT_SETTINGS.DIGITS_SMALL,
    DIGITS_LARGE: DEFAULT_SETTINGS.DIGITS_LARGE,
    CURRENCY_SYMBOL: DEFAULT_SETTINGS.CURRENCY_SYMBOL,
    BASE_CURRENCY: DEFAULT_SETTINGS.BASE_CURRENCY
};

export const defaultSetting = <K extends SettingsKey>(key: K) => {
    return DEFAULT_SETTINGS[key].value as Settings[K]['value'];
};
